import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { ReferenceDataField } from '@moment/trading-types/instrument-data'
import type { QuoteSideKey, SecurityWhere } from './security-types'

import { Operator } from '@moment/trading-types/filter'
import { BondStatus, BondType } from '@moment/trading-types/reference-data'

import { securityReadField } from './security-duration-column'
import { UnsupportedSecurityTranslationError } from './security-types'
import {
  bookFields,
  SECURITY_ENTITY,
  SECURITY_ID_FIELD,
} from './security-wire-fields'

export interface SecurityFilterOptions {
  /** Book side that `price`, `yield_to_worst` and `yield_to_maturity` read. */
  side: QuoteSideKey
}

const COMPOUND_KEYS = new Set(['and', 'or', 'not'])

const FILTER_LISTS_FIELD = 'filter_lists'

// Live inventory's `filter_lists` holds the lists naming a bond's ISIN, CUSIP
// or ticker, whatever field each list was built on.
const FILTER_LIST_KEY_FIELDS = [SECURITY_ID_FIELD, 'cusip', 'bond_ticker']

// Live inventory matches a ticker list against the bond ticker; Security's
// `ticker` prefers the equity symbol.
const FILTER_LIST_OPERAND_FIELDS: ReadonlyMap<string, string> = new Map([
  ['ticker', 'bond_ticker'],
])
const FILTER_LIST_OPERATORS = new Set<string>([
  Operator.InFilterList,
  Operator.NotInFilterList,
])

/** Security field behind a live-inventory filter or objective field name. */
export function securityFieldForInstrumentField(
  field: string,
  side: QuoteSideKey,
): string | undefined {
  const book = bookFields(side)
  switch (field) {
    case 'price':
      return book.price
    case 'yield_to_worst':
      return book.ytw
    case 'yield_to_maturity':
      return book.ytm
    case '144a':
      return 'is_144a'
    default:
      return securityReadField(field as ReferenceDataField)
  }
}

export function qualifySecurityField(field: string): string {
  return `${SECURITY_ENTITY}.${field}`
}

/**
 * Translates a live-inventory `FilterObject` to a SORM `where` over Security.
 * Throws `UnsupportedSecurityTranslationError` for a field or operator Security
 * cannot express, so a filter is never silently dropped.
 */
export function translateFilterObject(
  filter: FilterObject,
  options: SecurityFilterOptions,
): SecurityWhere {
  const clauses: SecurityWhere[] = []
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined) continue
    if (COMPOUND_KEYS.has(key)) {
      clauses.push(translateCompound(key, value, options))
      continue
    }
    clauses.push(...translateFieldCondition(key, value, options))
  }
  return combineAnd(clauses)
}

function translateCompound(
  key: string,
  value: unknown,
  options: SecurityFilterOptions,
): SecurityWhere {
  if (key === 'not') {
    return { not: translateFilterObject(value as FilterObject, options) }
  }
  const children = (value as FilterObject[])
    .map((child) => translateFilterObject(child, options))
    .filter((child) => Object.keys(child).length > 0)
  return { [key]: children }
}

function translateFieldCondition(
  field: string,
  condition: unknown,
  options: SecurityFilterOptions,
): SecurityWhere[] {
  if (field === FILTER_LISTS_FIELD) {
    return Object.entries(condition as Record<string, unknown>)
      .filter(([, value]) => value !== undefined)
      .map(([operator, value]) => translateFilterListsOperator(operator, value))
  }
  const securityField = securityFieldForInstrumentField(field, options.side)
  if (!securityField) {
    throw new UnsupportedSecurityTranslationError(
      `No Security field for live-inventory filter field "${field}"`,
    )
  }
  return Object.entries(condition as Record<string, unknown>)
    .filter(([, value]) => value !== undefined)
    .map(([operator, value]) => {
      const target =
        (FILTER_LIST_OPERATORS.has(operator)
          ? FILTER_LIST_OPERAND_FIELDS.get(field)
          : undefined) ?? securityField
      const clause = translateOperator(
        qualifySecurityField(target),
        target,
        operator,
        value,
        field,
      )
      return field === STATUS_FIELD && matchesOutstanding(operator, value)
        ? { or: [clause, MONEY_MARKET_WITHOUT_STATUS] }
        : clause
    })
}

const STATUS_FIELD = 'status'

// Security serves a money-market fund from fund reference data, which has no
// status; live inventory serves it as an outstanding bond.
const MONEY_MARKET_WITHOUT_STATUS: SecurityWhere = {
  and: [
    {
      [qualifySecurityField('asset_class')]: { equal_to: BondType.MoneyMarket },
    },
    { [qualifySecurityField(STATUS_FIELD)]: { is_null: true } },
  ],
}

function matchesOutstanding(operator: string, value: unknown): boolean {
  const values: unknown[] = [value].flat()
  switch (operator as Operator) {
    case Operator.EqualTo:
    case Operator.In:
      return values.includes(BondStatus.Outstanding)
    case Operator.NotEqualTo:
    case Operator.NotIn:
      return !values.includes(BondStatus.Outstanding)
    default:
      return false
  }
}

// Live inventory treats NULL as +infinity (perpetuals) for these fields: min
// filters keep a NULL row.
export const INFINITE_WHEN_NULL_FIELDS: ReadonlySet<string> = new Set([
  'maturity_in_years',
  'maturity_date',
  'workout_in_years',
  'workout_date',
  'next_call_in_years',
  'next_call_date',
])

function translateOperator(
  qualified: string,
  securityField: string,
  operator: string,
  value: unknown,
  liveInventoryField: string,
): SecurityWhere {
  const leaf = (op: string, operand: unknown = value): SecurityWhere => ({
    [qualified]: { [op]: operand },
  })
  // SQL negations drop NULL rows; live inventory keeps them.
  const orNull = (clause: SecurityWhere): SecurityWhere => ({
    or: [clause, leaf(Operator.IsNull, true)],
  })
  switch (operator as Operator) {
    case Operator.EqualTo:
      return value === null ? leaf(Operator.IsNull, true) : leaf(operator)
    case Operator.NotEqualTo:
      return value === null
        ? leaf(Operator.IsNull, false)
        : orNull(leaf(operator))
    case Operator.NotIn:
      return orNull(leaf(operator))
    case Operator.GreaterThan:
    case Operator.GreaterThanOrEqualTo:
      return INFINITE_WHEN_NULL_FIELDS.has(liveInventoryField)
        ? orNull(leaf(operator))
        : leaf(operator)
    case Operator.LessThan:
    case Operator.LessThanOrEqualTo:
    case Operator.In:
    case Operator.IsNull:
    case Operator.InFilterList:
    case Operator.NotInFilterList:
      return leaf(operator)
    case Operator.StartsWith:
      return leaf('istarts_with')
    case Operator.EndsWith:
      return leaf('iends_with')
    case Operator.Includes:
      return leaf('includes_case_insensitive')
    case Operator.NotIncludes:
      return orNull({ not: leaf('includes_case_insensitive') })
    case Operator.NotStartsWith:
      return { not: leaf('starts_with') }
    case Operator.NotInList:
      break
  }
  throw new UnsupportedSecurityTranslationError(
    `No Security translation for filter operator "${operator}" on "${securityField}"`,
  )
}

function filterListIds(operator: string, value: unknown): string[] {
  const values: unknown[] = [value].flat(2)
  const ids = values.filter((id): id is string => typeof id === 'string')
  if (ids.length === 0 || ids.length !== values.length) {
    throw new UnsupportedSecurityTranslationError(
      `Filter "${operator}" on "${FILTER_LISTS_FIELD}" needs list IDs`,
    )
  }
  return ids
}

// A row is on a list when its ISIN, CUSIP or ticker is; an array operand
// names several lists and the row matches on any of them.
function translateFilterListsOperator(
  operator: string,
  value: unknown,
): SecurityWhere {
  const keyLeaves = (op: string) =>
    filterListIds(operator, value).flatMap((listId) =>
      FILTER_LIST_KEY_FIELDS.map((field) => ({
        [qualifySecurityField(field)]: { [op]: listId },
      })),
    )
  switch (operator as Operator) {
    case Operator.Includes:
    case Operator.EqualTo:
    case Operator.In:
      return { or: keyLeaves('in_filter_list') }
    case Operator.NotIncludes:
    case Operator.NotEqualTo:
    case Operator.NotIn:
      return { and: keyLeaves('not_in_filter_list') }
    default:
      throw new UnsupportedSecurityTranslationError(
        `No Security translation for filter operator "${operator}" on "${FILTER_LISTS_FIELD}"`,
      )
  }
}

/** ANDs clauses, dropping empty ones; an empty list matches every row. */
export function combineAnd(clauses: SecurityWhere[]): SecurityWhere {
  const nonEmpty = clauses.filter((clause) => Object.keys(clause).length > 0)
  const [only, ...rest] = nonEmpty
  if (!only) return {}
  if (rest.length === 0) return only
  return { and: nonEmpty }
}
